import React from "react";

import { compact, isEmpty, map, stubFalse } from "lodash/fp";

import type { VideoPlayerAction } from "@app/analytics/types";

import { Row } from "@rui/Stack";

import PlayerControlsSettings, {
  PlayerControlsSettingsProps,
} from "./PlayerControlsSettings";
import { Wrapper, CurrentTime, SegmentLabel } from "./styles";
import type { Block, Marker, Segment } from "./types";
import ScrubberBar from "../ScrubberBar";
import { doesIntersectTimestamp } from "../ScrubberBar/utils";
import VideoIconButton from "../VideoIconButton";

interface AdditionalAction {
  action: VideoPlayerAction;
  disabled?: boolean;
}

interface PlayerControlsProps {
  additionalActions?: AdditionalAction[];
  blocks?: Block[];
  currentTime?: number;
  duration?: number;
  expandable?: boolean;
  expanded?: boolean;
  loadingAction?: VideoPlayerAction;
  markers?: Marker[];
  muted: boolean;
  onActionClick: (type: VideoPlayerAction) => void;
  onClickBlockOrMarker?: (timestamp: number) => void;
  onScrubberChange: (seconds: number) => void;
  paused: boolean;
  segments?: Segment[];
  renderSettings?: PlayerControlsSettingsProps["renderSettings"];
  settingsTargetClassName?: PlayerControlsSettingsProps["targetClassName"];
  withAnnotations?: boolean;
}

const BUTTON_SIZE = 30; // 24px + margin

const alreadyPastAllHighlights = (
  currentTime: PlayerControlsProps["currentTime"],
  blocks: PlayerControlsProps["blocks"] = [],
  markers: PlayerControlsProps["markers"] = []
) => {
  if (currentTime == null) return true;
  const noMoreBlocks = blocks.every((b) => b.startTime < currentTime);
  const noMoreMarkers = markers.every((m) => m.startTime < currentTime);
  return noMoreBlocks && noMoreMarkers;
};

const shouldShowControl = (
  actions: PlayerControlsProps["additionalActions"] = []
) => {
  if (isEmpty(actions)) return stubFalse;
  const actionNames = map("action", actions);

  return (action: VideoPlayerAction) => actionNames.includes(action);
};

const isControlDisabled = (
  actions: PlayerControlsProps["additionalActions"] = [],
  action: VideoPlayerAction
) => {
  const config = actions.find((a) => a.action === action);
  return Boolean(!config || config.disabled);
};

const PlayerControls = ({
  additionalActions = [],
  blocks,
  currentTime,
  duration,
  expandable = false,
  expanded = false,
  loadingAction,
  markers,
  muted,
  onActionClick,
  onClickBlockOrMarker,
  onScrubberChange,
  paused,
  renderSettings,
  settingsTargetClassName,
  segments,
  withAnnotations = false,
}: PlayerControlsProps) => {
  const handleActionClick =
    (type: VideoPlayerAction) => (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      onActionClick(type);
    };

  const showHighlightNavigation =
    (!isEmpty(blocks) || !isEmpty(markers)) && withAnnotations;

  const disableNextHighlightButton =
    showHighlightNavigation &&
    alreadyPastAllHighlights(currentTime, blocks, markers);

  const currentSegment = (segments ?? []).find(
    (s) => s.label && doesIntersectTimestamp(s, currentTime ?? 0)
  );

  const showAction = shouldShowControl(additionalActions);

  // kinda dumb (but effective!) way to determine how many buttons we're showing on the right-hand
  // side. each entry in the array corresponds to the condition that will show a button (mute/unmute
  // is always visible)
  const rightButtonMenuSize =
    compact(["mute", renderSettings, showAction("fullScreen"), expandable])
      .length * BUTTON_SIZE;

  return (
    <Wrapper className="player-controls" onClick={(e) => e.stopPropagation()}>
      <ScrubberBar
        blocks={blocks}
        currentTime={currentTime ?? 0}
        duration={duration ?? 0}
        markers={markers}
        onChange={onScrubberChange}
        onClickBlockOrMarker={onClickBlockOrMarker}
        segments={segments}
        withAnnotations={withAnnotations}
      />
      <Row
        className="player-controls__controls"
        variant="apart"
        spacing={1}
        sx={{ p: "2px 4px" }}
      >
        <Row
          variant="left"
          spacing="10px"
          sx={{
            maxWidth: `calc(100% - ${rightButtonMenuSize}px)`,
            overflow: "hidden",
          }}
          className="player-controls__controls__left"
        >
          {paused ? (
            <VideoIconButton
              icon="play"
              tooltip="Play"
              onClick={handleActionClick("play")}
              loading={loadingAction === "play"}
            />
          ) : (
            <VideoIconButton
              icon="pause"
              tooltip="Pause"
              onClick={handleActionClick("pause")}
            />
          )}
          {showAction("previous") && (
            <VideoIconButton
              icon="previous"
              onClick={handleActionClick("previous")}
              disabled={isControlDisabled(additionalActions, "previous")}
            />
          )}
          {showAction("next") && (
            <VideoIconButton
              icon="next"
              onClick={handleActionClick("next")}
              disabled={isControlDisabled(additionalActions, "next")}
            />
          )}
          {showAction("skipBackward10") && (
            <VideoIconButton
              icon="skipBackward10"
              onClick={handleActionClick("skipBackward10")}
            />
          )}
          {showAction("skipForward10") && (
            <VideoIconButton
              icon="skipForward10"
              onClick={handleActionClick("skipForward10")}
            />
          )}
          {showHighlightNavigation && (
            <VideoIconButton
              disabled={disableNextHighlightButton}
              icon="nextHighlight"
              tooltip="Next highlight"
              onClick={handleActionClick("nextHighlight")}
            />
          )}
          <Row variant="left" spacing="4px" sx={{ overflow: "hidden" }}>
            <CurrentTime currentTime={currentTime} duration={duration} />
            {currentSegment && withAnnotations && (
              <SegmentLabel>• {currentSegment.label}</SegmentLabel>
            )}
          </Row>
        </Row>
        <Row
          variant="right"
          spacing="10px"
          className="player-controls__controls__right"
        >
          {muted ? (
            <VideoIconButton
              icon="unmute"
              tooltip="Unmute"
              onClick={handleActionClick("unmute")}
            />
          ) : (
            <VideoIconButton
              icon="mute"
              tooltip="Mute"
              onClick={handleActionClick("mute")}
            />
          )}
          <PlayerControlsSettings
            renderSettings={renderSettings}
            targetClassName={settingsTargetClassName}
          />
          {showAction("fullScreen") && (
            <VideoIconButton
              icon="fullScreen"
              tooltip="Full screen"
              onClick={handleActionClick("fullScreen")}
            />
          )}
          {expandable &&
            (expanded ? (
              <VideoIconButton
                icon="minimize"
                onClick={handleActionClick("minimize")}
              />
            ) : (
              <VideoIconButton
                icon="maximize"
                onClick={handleActionClick("maximize")}
              />
            ))}
        </Row>
      </Row>
    </Wrapper>
  );
};

export default PlayerControls;
